import React, { useEffect, useRef, useState } from "react";
import { SwatchesPicker } from "react-color";
import { chunkArray } from "../../utils/basic.utils";
import { ConventionalColorNames } from "../color.codes";

interface TailwindColorPickerProps {
  colorCode: string;
  colorName: string;
  onColorCodeChange: (colorValue: string) => void;
  onColorNameChange: (value: string) => void;
  placeholder: string;
  id?: string;
}

const conventionalColorsHexOnly = Object.keys(ConventionalColorNames);
const chunkedHexColors = chunkArray(conventionalColorsHexOnly, 4);

const TailwindColorPicker = ({
  colorCode,
  colorName,
  onColorCodeChange,
  onColorNameChange,
  placeholder,
  id,
}: TailwindColorPickerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeColorCode, setActiveColorCode] = useState(
    colorCode || "#333333"
  );
  const [activeColorName, setActiveColorName] = useState(colorName || "");

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setShowColorPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setActiveColorName(newValue);
    onColorNameChange(newValue);

    // Find color by name in ConventionalColorNames
    const sanitizedValue = newValue.toLowerCase().trim();
    const colorMatch = Object.entries(ConventionalColorNames).find(
      ([_, colorName]) => colorName.toLowerCase() === sanitizedValue
    );

    if (colorMatch) {
      setActiveColorCode(colorMatch[0]);
      onColorCodeChange(colorMatch[0]);
    }
  };

  const handleColorChange = (color: { hex: string }) => {
    setActiveColorCode(color.hex);
    onColorCodeChange(color.hex);

    // Find color name by hex in ConventionalColorNames
    const hexValue = color.hex.toUpperCase();
    const colorName =
      ConventionalColorNames[hexValue] ||
      ConventionalColorNames[hexValue.toLowerCase()];

    if (colorName) {
      setActiveColorName(colorName);
      onColorNameChange(colorName);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center space-x-3 mb-2">
        <button
          onClick={() => setShowColorPicker(!showColorPicker)}
          className="w-10 h-10 rounded-full border-2 border-gray-300 cursor-pointer transition-all hover:border-gray-400"
          style={{ backgroundColor: activeColorCode }}
        />
        <input
          id={id}
          type="text"
          value={activeColorName}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="flex-1 bg-transparent border-0 border-b border-gray-300 focus:border-blue-500 focus:ring-0 outline-none px-0 py-2 text-gray-900"
        />
      </div>

      {showColorPicker && (
        <div className="absolute z-50 top-full left-0 mt-2">
          <SwatchesPicker
            color={activeColorCode}
            onChange={handleColorChange}
            colors={chunkedHexColors}
            width={224}
          />
        </div>
      )}
    </div>
  );
};

export default TailwindColorPicker;
